import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Heading, Select, Tabs } from "@foris/avocado-suite";
import RejectProposalModal from "@components/reject-proposal-modal";
import { useLayoutStore } from "@context/useLayoutStore";
import { AssignmentProposalErrorCode, type AssignmentProposal } from "@models/ISchema";
import { userDisplayName } from "@services/collections/assignment-proposal/assignmentProposalAdapters";
import { useDeleteAssignmentProposal } from "@services/collections/assignment-proposal/assignmentProposalListServices";
import { useRejectAssignmentProposal } from "@services/collections/assignment-proposal/assignmentProposalServices";
import { useGroupProposalLists, type ProposalListState } from "../../hooks/useGroupProposalLists";
import { useProposalCardAction } from "../../hooks/useProposalCardAction";
import DeleteDraftModal from "../delete-draft-modal";
import ProposalCard from "../proposal-card";
import ProposalList from "../proposal-list";
import { toProposalCardModel } from "../../utils/proposalCard";
import styles from "./groupProposalsDrawer.module.scss";

interface ProposalsLink {
  id: string;
  code?: string | null;
}

interface GroupProposalsDrawerProps {
  isOpen: boolean;
  /** Links the group belongs to. Proposals belong to a link, not to the group. */
  links: ProposalsLink[];
  scenarioId: string;
  originId: string;
  /** The user sends proposals: "Enviadas" and "Borradores". */
  canSubmit: boolean;
  /** The user reviews proposals: "Por revisar". */
  canReview: boolean;
  /** The link the header dot leads to: the Drawer opens on it and on "Por revisar". */
  reviewLinkId?: string | null;
  onClose: () => void;
  /** Opens the assignation editor of the link for one of its proposals, or for its review. */
  onOpenProposal: (linkId: string, proposalId: string, options: { review: boolean }) => void;
  /** Tells the parent whether "Por revisar" of a link has proposals (the header dot uses it). */
  onReviewLoaded?: (linkId: string, hasPending: boolean) => void;
}

interface LinkOption {
  label: string;
  value: string;
}

type TabKey = "sent" | "drafts" | "review";

/** The draft is gone or was sent meanwhile: the list on screen is out of date. */
const STALE_DRAFT_CODES: readonly string[] = [
  AssignmentProposalErrorCode.ProposalNotFound,
  AssignmentProposalErrorCode.ProposalNotEditable,
];

/**
 * "Solicitudes de cambio": the proposals of a link by role. "Enviadas" and "Borradores" list the
 * user's own proposals (drafts can be deleted from their card), and "Por revisar" the ones waiting
 * for the user's review (they can be rejected from their card).
 */
const GroupProposalsDrawer = ({
  isOpen,
  links,
  scenarioId,
  originId,
  canSubmit,
  canReview,
  reviewLinkId = null,
  onClose,
  onOpenProposal,
  onReviewLoaded,
}: GroupProposalsDrawerProps) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const opensOnReview = canReview && !!reviewLinkId;
  const initialLinkId = opensOnReview ? reviewLinkId : (links?.[0]?.id ?? null);
  const initialTab: TabKey = opensOnReview || !canSubmit ? "review" : "sent";
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(initialLinkId);
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const lists = useGroupProposalLists({
    isOpen,
    linkId: selectedLinkId,
    scenarioId,
    originId,
    canSubmit,
    canReview,
    onReviewLoaded,
  });
  const rejection = useProposalCardAction(useRejectAssignmentProposal(), {
    scenarioId,
    originId,
    linkId: selectedLinkId,
    successKind: "recorded",
    successKey: "assignation-edit.proposal.toast.rejected",
    intent: "reject",
    reload: lists.reloadReview,
  });
  const deletion = useProposalCardAction(useDeleteAssignmentProposal(), {
    scenarioId,
    originId,
    linkId: selectedLinkId,
    successKind: "deleted",
    successKey: "assignation-edit.proposal.toast.draft-deleted",
    intent: "delete",
    reload: lists.reloadOwn,
    reloadOn: STALE_DRAFT_CODES,
  });
  const { close: closeRejection } = rejection;
  const { close: closeDeletion } = deletion;

  const linkOptions = useMemo<LinkOption[]>(
    () =>
      (links ?? []).map(link => ({
        label: t("group-proposals.link-selector.option", { code: link.code ?? link.id }),
        value: link.id,
      })),
    [links, t],
  );

  // Every opening starts on the initial link and tab. They are reset while closed, so opening
  // sends a single request per list.
  useEffect(() => {
    if (isOpen) return;

    setSelectedLinkId(initialLinkId);
    setActiveTab(initialTab);
    closeRejection();
    closeDeletion();
  }, [isOpen, initialLinkId, initialTab, closeRejection, closeDeletion]);

  // Only review cards can be rejected: the modal names who sent that proposal.
  const rejectingRequester = lists.review.proposals.find(
    proposal => proposal.id === rejection.targetId,
  )?.createdBy;

  const renderCard = (proposal: AssignmentProposal, isReview: boolean) => (
    <ProposalCard
      proposal={toProposalCardModel(proposal, { isReview })}
      onAction={() => onOpenProposal(selectedLinkId, proposal.id, { review: isReview })}
      onReject={() => rejection.open(proposal.id)}
      onDelete={() => deletion.open(proposal.id)}
    />
  );

  const listTab = (key: TabKey, list: ProposalListState, onRetry: () => void) => ({
    key,
    label: t(`group-proposals.tabs.${key}`),
    children: (
      <ProposalList
        {...list}
        emptyKey={`group-proposals.empty.${key}`}
        onRetry={onRetry}
        renderCard={proposal => renderCard(proposal, key === "review")}
      />
    ),
  });
  const tabs = [
    ...(canSubmit
      ? [
          listTab("sent", lists.sent, lists.reloadOwn),
          listTab("drafts", lists.drafts, lists.reloadOwn),
        ]
      : []),
    ...(canReview ? [listTab("review", lists.review, lists.reloadReview)] : []),
  ];
  // A single list (a reviewer who sends nothing) goes without tabs.
  const hasTabs = tabs.length > 1;
  const activeIndex = Math.max(
    tabs.findIndex(tab => tab.key === activeTab),
    0,
  );

  return (
    <>
      <Drawer
        className={styles.groupProposalsDrawer}
        title={<Heading type="h3">{t("group-proposals.title")}</Heading>}
        isOpen={isOpen}
        onClose={onClose}
        heightGap={visibleHeaderPixels}
        bodyPadding={false}
        closeOnOverlayClick
      >
        <div className={styles.groupProposalsDrawer_content}>
          {linkOptions.length > 1 && (
            <Select
              label={t("group-proposals.link-selector.label")}
              options={linkOptions}
              value={linkOptions.find(option => option.value === selectedLinkId) ?? null}
              onChange={(option: LinkOption | null) => {
                if (option?.value) setSelectedLinkId(option.value);
              }}
            />
          )}

          {hasTabs ? (
            <Tabs
              className={styles.groupProposalsDrawer_tabs}
              activeIndex={activeIndex}
              onClick={index => setActiveTab(tabs[index].key)}
              items={tabs}
            />
          ) : (
            tabs[0]?.children
          )}
        </div>
      </Drawer>

      {/* Outside the Drawer: its content has a transform, which would pin the modals to the Drawer
          instead of the window and leave them off screen. */}
      {!!rejection.targetId && (
        <RejectProposalModal
          requesterName={rejectingRequester ? userDisplayName(rejectingRequester) : null}
          isSubmitting={rejection.isSubmitting}
          errorMessage={rejection.error}
          onCancel={rejection.close}
          onConfirm={reason => rejection.confirm({ reason })}
        />
      )}

      {!!deletion.targetId && (
        <DeleteDraftModal
          isSubmitting={deletion.isSubmitting}
          errorMessage={deletion.error}
          onCancel={deletion.close}
          onConfirm={() => deletion.confirm({})}
        />
      )}
    </>
  );
};

export default GroupProposalsDrawer;
